import React, { useMemo, useState } from 'react';
import { Badge } from 'antd';
import moment, { Moment } from 'moment-timezone';
import classNames from 'classnames';
import { DeliveryWindow } from '@bringg/types';
import BringgCalendar from '../bringg-calendar/bringg-calendar';
import TimeWindowSelection from './components/time-window-selection';
import getAllowedMonthsNav from './helpers/get-allowed-months-nav';
import { DeliveryWindowCalendarProps, DateInfo } from './delivery-window-calendar.types';
import getStartOfDay from './helpers/get-start-of-day';
import styles from './delivery-window-calendar.module.scss';
import getStartEndTime from './helpers/get-start-end-time';
import { ScreenType } from '../../types/common.consts';

const DATE_FORMAT = 'YYYY-MM-DD';

const DeliveryWindowCalendar = ({
	initialDate,
	deliveryWindows,
	screenType,
	onDeliveryWindowSelected,
	className,
	locale = 'en',
	timezone = moment.tz.guess(),
	fullscreen = true,
	schedulingCustomerMessage,
	externalIdInfo
}: DeliveryWindowCalendarProps) => {
	const [currentDate, setCurrentDate] = useState(
		getStartOfDay({ date: initialDate, locale, timezone }).format(DATE_FORMAT)
	);

	const deliveryWindowsByDatesMap = useMemo(() => {
		const temp = new Map<string, DateInfo[]>();

		deliveryWindows.forEach(deliveryWindow => {
			const { end_time, start_time } = deliveryWindow;
			const dateKey = getStartOfDay({ date: start_time, locale, timezone }).format(DATE_FORMAT);
			const valueToPush = {
				text: getStartEndTime({
					startTime: start_time,
					endTime: end_time,
					locale,
					timezone
				}),
				deliveryWindow: deliveryWindow
			};
			const currentDate = temp.get(dateKey);
			if (currentDate) {
				currentDate.push(valueToPush);
			} else {
				temp.set(dateKey, [valueToPush]);
			}
		});

		return temp;
	}, []);

	const renderDesktopDate = (date: Moment) => {
		const deliveryWindows = deliveryWindowsByDatesMap.get(date.format(DATE_FORMAT));
		return (
			<ul className={styles.dateCell}>
				{deliveryWindows?.map((deliveryWindow, index) => (
					<li key={index} className={styles.deliveryWindowItem}>
						<Badge status={'success'} text={deliveryWindow.text} />
					</li>
				))}
			</ul>
		);
	};

	const shouldDisableDate = (date: Moment) => !deliveryWindowsByDatesMap.get(date.format(DATE_FORMAT));

	const navigationConfig = getAllowedMonthsNav({ locale, timezone });

	const onSelectedDate = (date: Moment) => {
		setCurrentDate(date.format(DATE_FORMAT));
	};

	const onTimeWindowSelected = (deliveryWindow: DeliveryWindow) => {
		onDeliveryWindowSelected(deliveryWindow);
	};

	return (
		<div className={classNames(styles.deliveryWindowCalendarContainer, className)}>
			<BringgCalendar
				screenType={screenType}
				locale={locale}
				timezone={timezone}
				selectionMode={'single'}
				value={currentDate}
				onChange={onSelectedDate}
				shouldDisableDate={shouldDisableDate}
				allowClear={false}
				className={classNames(styles.calendar, { [styles.mobileView]: screenType === ScreenType.MOBILE })}
				dateCellRender={screenType === ScreenType.DESKTOP && fullscreen && renderDesktopDate}
				fullscreen={fullscreen}
				{...navigationConfig}
			/>
			<TimeWindowSelection
				locale={locale}
				screenType={screenType}
				dates={deliveryWindowsByDatesMap.get(currentDate)}
				currentDate={currentDate}
				onTimeWindowSelected={onTimeWindowSelected}
				externalIdInfo={externalIdInfo}
				timezone={timezone}
				schedulingCustomerMessage={schedulingCustomerMessage}
			/>
		</div>
	);
};

export default DeliveryWindowCalendar;
